import React, { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import LoadingAnimation from "../Shared/LoadingAnimation/LoadingAnimation";

/**
 * Route protection component that verifies authentication
 * before rendering children components
 *
 * @param {Object} props Component props
 * @param {React.ReactNode} props.children Child components to render when authenticated
 * @param {Function} props.authCheck Optional custom auth check function
 * @returns {JSX.Element} Protected component or redirect
 */
const ProtectedRoute = ({ children, authCheck = null }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);

  useEffect(() => {
    const checkAuthentication = async () => {
      try {
        // Basic authentication check
        const token = localStorage.getItem("token");

        if (!token) {
          setIsAuthorized(false);
          return;
        }

        // If a custom auth check is provided, run it
        if (authCheck && typeof authCheck === "function") {
          const isValid = await authCheck();
          setIsAuthorized(isValid);
        } else {
          // Default: just check token exists
          setIsAuthorized(true);
        }
      } catch (error) {
        console.error("Auth check error:", error);
        setIsAuthorized(false);
      } finally {
        setIsChecking(false);
      }
    };

    checkAuthentication();
  }, [authCheck]);

  if (isChecking) {
    return <LoadingAnimation />;
  }

  if (!isAuthorized || !localStorage.getItem("token")) {
    // Save the location they were trying to visit
    return navigate("/", { state: { from: location.pathname } });
  }

  return children;
};

export default ProtectedRoute;
